// src/pages/intranet/pages/Secretaria.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FileText, FileDown, FolderOpen, Send, CheckCircle, Clock, Plus, X } from 'lucide-react';

export default function Secretaria() {
  const { user } = useAuth();
  
  const [documentos, setDocumentos] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Helper para convertir el rol en nivel numérico
  const getGradoNivel = (role) => {
    if (role === 'ADMIN') return 4;
    if (role === 'MAESTRO') return 3;
    if (role === 'COMPAÑERO') return 2;
    return 1; // APRENDIZ por defecto
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user?.role) {
      cargarDocumentos();
    }
  }, [user]);

  const cargarDocumentos = async () => {
    try {
      const nivel = getGradoNivel(user?.role);
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/secretaria/documentos?grado_nivel=${nivel}`);
      const data = await res.json();
      if (data.success) {
        setDocumentos(data.data);
      }
    } catch (error) {
      console.error("Error cargando documentos:", error);
    }
  };

  const handleSubirDocumento = async (formData) => {
    try {
      // Usamos fetch directo con FormData porque contiene un archivo físico
      const res = await fetch('https://logia-production-725b.up.railway.app/api/secretaria/documentos', {
        method: 'POST',
        body: formData // No se envía Content-Type, el navegador lo asigna automáticamente con el boundary
      });
      const data = await res.json();
      
      if (data.success) {
        alert('Documento subido correctamente al archivo.');
        setIsUploadModalOpen(false);
        cargarDocumentos(); // Refresca las tablas
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      alert('Error de conexión al subir el archivo.');
    }
  };

  return (
    <div className="flex flex-col gap-6 lg:gap-8 animate-fade-in relative">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-white m-0 tracking-wide">Secretaría de Logia</h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Gestión de actas y documentos institucionales</p>
        </div>
        
        {/* Solo Maestros y Admins pueden subir nuevas actas */}
        {(user?.role === 'MAESTRO' || user?.role === 'ADMIN') && (
          <button 
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center gap-2 bg-[#C5A059] hover:bg-white text-[#050505] px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors shadow-lg"
          >
            <Plus size={16} /> Subir Documento
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        
        {/* COLUMNA IZQUIERDA: REGISTRO DE ACTAS */}
        <div className="bg-[#0a0a0a] border border-white/10 shadow-2xl relative overflow-hidden flex flex-col">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
          
          <div className="p-6 border-b border-white/10 bg-[#050505]/50 flex items-center gap-3 relative z-10">
            <FolderOpen size={20} className="text-[#C5A059]" />
            <h2 className="font-serif text-2xl text-white m-0">Registro de Actas</h2>
          </div>
          
          <div className="p-6 md:p-8 flex flex-col gap-10 relative z-10">
            {/* Actas de Primer Grado - Visible para TODOS */}
            <ActasTable 
              gradoNombre="Primer Grado (Aprendices)" 
              nivelRequerido={1} 
              data={documentos} 
            />

            {/* Actas de Segundo Grado - Visible para Compañeros, Maestros y Admins */}
            {(user?.role === 'COMPAÑERO' || user?.role === 'MAESTRO' || user?.role === 'ADMIN') && (
              <ActasTable 
                gradoNombre="Segundo Grado (Compañeros)" 
                nivelRequerido={2} 
                data={documentos} 
              />
            )}

            {/* Actas de Tercer Grado - Visible para Maestros y Admins */}
            {(user?.role === 'MAESTRO' || user?.role === 'ADMIN') && (
              <ActasTable 
                gradoNombre="Tercer Grado (Maestros)" 
                nivelRequerido={3} 
                data={documentos} 
              />
            )}
          </div>
        </div>

        {/* COLUMNA DERECHA: DOCUMENTOS GENERALES */}
        <div className="flex flex-col gap-8">
          
          {/* Documentos Recibidos */}
          <div className="bg-[#0a0a0a] border border-white/10 shadow-xl relative overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/10 bg-[#050505]/50 flex items-center gap-3 relative z-10">
              <FileDown size={20} className="text-blue-400" />
              <h2 className="font-serif text-xl text-white m-0">Documentos Recibidos</h2>
            </div>
            <div className="p-0 relative z-10">
              <DocumentosGeneralesTable tipo="recibido" data={documentos} />
            </div>
          </div>

          {/* Documentos Enviados */}
          <div className="bg-[#0a0a0a] border border-white/10 shadow-xl relative overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/10 bg-[#050505]/50 flex items-center gap-3 relative z-10">
              <Send size={18} className="text-[#C5A059]" />
              <h2 className="font-serif text-xl text-white m-0">Documentos Enviados</h2>
            </div>
            <div className="p-0 relative z-10">
              <DocumentosGeneralesTable tipo="enviado" data={documentos} />
            </div>
          </div>

        </div>

      </div>

      {/* MODAL DE SUBIDA DE DOCUMENTOS */}
      {isUploadModalOpen && (
        <SubirDocumentoModal 
          onClose={() => setIsUploadModalOpen(false)} 
          onSave={handleSubirDocumento} 
          usuarioId={user?.id}
        />
      )}
    </div>
  );
}

// --- COMPONENTES SECUNDARIOS DINÁMICOS ---

function ActasTable({ gradoNombre, nivelRequerido, data }) {
  // Filtramos localmente las actas que corresponden a este nivel exacto
  const actas = data.filter(doc => doc.tipo_documento === 'acta' && doc.grado_requerido === nivelRequerido);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h3 className="font-serif text-lg text-white m-0">{gradoNombre}</h3>
        <div className="h-[1px] flex-grow bg-white/10"></div>
      </div>
      
      <div className="overflow-x-auto border border-white/5 bg-[#050505]">
        <table className="w-full text-left border-collapse min-w-[500px]">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">N° Acta</th>
              <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</th>
              <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-center">Estado</th>
              <th className="py-3 px-4 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-right">Documento</th>
            </tr>
          </thead>
          <tbody>
            {actas.map((acta) => (
              <tr key={acta.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="py-3 px-4 text-white text-sm font-medium">{acta.numero_documento || 'S/N'}</td>
                <td className="py-3 px-4 text-gray-400 text-xs font-mono">
                  {new Date(acta.fecha_documento).toLocaleDateString('es-PE')}
                </td>
                <td className="py-3 px-4 text-center">
                  {acta.estado === 'aprobada' ? (
                    <span className="inline-flex items-center gap-1 bg-green-900/20 border border-green-800/50 text-green-500 px-2 py-1 text-[9px] uppercase tracking-widest font-mono">
                      <CheckCircle size={10}/> Aprobada
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-yellow-900/20 border border-yellow-800/50 text-yellow-500 px-2 py-1 text-[9px] uppercase tracking-widest font-mono">
                      <Clock size={10}/> Pendiente
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  <button 
                    onClick={() => window.open(acta.archivo_url, '_blank')}
                    className="bg-transparent border border-white/20 text-gray-400 hover:text-white hover:bg-white/10 px-3 py-1 text-[10px] tracking-widest uppercase transition-colors inline-flex items-center gap-2"
                  >
                    <FileText size={12}/> PDF
                  </button>
                </td>
              </tr>
            ))}
            {actas.length === 0 && (
              <tr>
                <td colSpan="4" className="py-4 text-center text-gray-600 text-xs tracking-widest uppercase">
                  No hay actas registradas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DocumentosGeneralesTable({ tipo, data }) {
  // Filtramos recibidos o enviados
  const documentos = data.filter(doc => doc.tipo_documento === tipo);

  return (
    <div className="overflow-x-auto bg-[#050505]">
      <table className="w-full text-left border-collapse min-w-[500px]">
        <thead>
          <tr className="border-b border-white/10 bg-white/5">
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">
              {tipo === 'recibido' ? 'Remitente' : 'Dirigido a'}
            </th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Asunto</th>
            <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-right">Archivo</th>
          </tr>
        </thead>
        <tbody>
          {documentos.map((doc) => (
            <tr key={doc.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              <td className="py-4 px-6 text-gray-400 text-xs font-mono">
                {new Date(doc.fecha_documento).toLocaleDateString('es-PE')}
              </td>
              <td className="py-4 px-6 text-gray-300 text-sm font-medium">{doc.remitente_destinatario || '-'}</td>
              <td className="py-4 px-6 text-white text-sm">{doc.asunto}</td>
              <td className="py-4 px-6 text-right">
                <button 
                  onClick={() => window.open(doc.archivo_url, '_blank')}
                  className="bg-transparent border border-white/20 text-gray-400 hover:text-white hover:bg-white/10 px-3 py-1 text-[10px] tracking-widest uppercase transition-colors inline-flex items-center gap-2"
                >
                  <FileText size={12}/> Ver
                </button>
              </td>
            </tr>
          ))}
          {documentos.length === 0 && (
            <tr>
              <td colSpan="4" className="py-6 text-center text-gray-600 text-xs tracking-widest uppercase">
                No hay documentos registrados
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- MODAL DE CARGA DE ARCHIVOS ---

function SubirDocumentoModal({ onClose, onSave, usuarioId }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tipo_documento: 'acta',
    grado_requerido: '1',
    numero_documento: '',
    fecha_documento: new Date().toISOString().split('T')[0],
    asunto: '',
    remitente_destinatario: ''
  });
  const [archivo, setArchivo] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleFileChange = (e) => setArchivo(e.target.files[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!archivo) {
      alert("Por favor selecciona un archivo PDF.");
      return;
    }

    setLoading(true);
    
    // Al incluir un archivo físico, debemos instanciar un FormData nativo
    const submitData = new FormData();
    Object.keys(formData).forEach(key => submitData.append(key, formData[key]));
    submitData.append('archivo', archivo);
    submitData.append('usuario_id', usuarioId || 'admin');

    await onSave(submitData);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/90 backdrop-blur-md flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-lg p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">Ingresar al Archivo</h2>
          <button onClick={onClose} disabled={loading} className="bg-transparent border-none text-gray-500 hover:text-white transition-colors cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Tipo de Registro</label>
              <select name="tipo_documento" value={formData.tipo_documento} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none">
                <option value="acta">Acta de Tenida</option>
                <option value="recibido">Documento Recibido</option>
                <option value="enviado">Documento Enviado</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Grado Requerido</label>
              <select name="grado_requerido" value={formData.grado_requerido} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none">
                <option value="1">1° - Aprendiz</option>
                <option value="2">2° - Compañero</option>
                <option value="3">3° - Maestro</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Número (Opcional)</label>
              <input type="text" name="numero_documento" placeholder="Ej. 001-2026" value={formData.numero_documento} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Fecha</label>
              <input type="date" name="fecha_documento" required value={formData.fecha_documento} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" style={{ colorScheme: 'dark' }} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Asunto Principal</label>
            <input type="text" name="asunto" required placeholder="Descripción breve" value={formData.asunto} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Remitente / Destinatario (Opcional)</label>
            <input type="text" name="remitente_destinatario" placeholder="Para correspondencia" value={formData.remitente_destinatario} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>

          <div className="flex flex-col gap-2 border border-dashed border-white/20 p-4 bg-white/5 relative mt-2 hover:border-[#C5A059] transition-colors">
            <input type="file" required accept="application/pdf" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            <div className="flex flex-col items-center justify-center pointer-events-none">
              <FileDown size={24} className="text-[#C5A059] mb-2" />
              <span className="text-white text-sm font-medium">{archivo ? archivo.name : 'Haz clic para seleccionar archivo PDF'}</span>
              <span className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Límite recomendado: 10MB</span>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" disabled={loading} onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors flex items-center gap-2 disabled:opacity-50">
              {loading ? <Clock size={14} className="animate-spin" /> : <Send size={14} />} 
              {loading ? 'Subiendo...' : 'Guardar en Archivo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}